import {useEffect, useState, useCallback} from 'react';
import {Button, Form, Modal, Nav, OverlayTrigger, Tab, Table, Tooltip} from 'react-bootstrap';
import axios from 'axios';
import {joinURLs} from '../../utils/urlUtil';
import {getHeadersConfig} from '../../utils/config';
import {IncludeExclude} from '../../utils/types';
import dayjs from '../../utils/dayjs';
import Loading from '../../utils/Loding';
import { PagesHelper } from '../../common/PagesHelper';
import StateHelper from '../../utils/StateHelper';
import InfoIcon from '../clinks/info/InfoIcon';
import './StatsModal.css';

interface StatsModalProps {
  show: boolean;
  onHide: () => void;
  app: any;
  currentUser?: any;
  selectedCampaign?: any; // Campaign that was selected to open the modal
}

interface StatsData {
  day?: any[];
  hour?: any[];
  day_hour?: any[];
  publisher?: any[];
  country?: any[];
  clink?: any[];
  campaign?: any[];
}

const StatsModal = ({show, onHide, app, currentUser, selectedCampaign: selectedCampaignProp}: StatsModalProps) => {
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [data, setData] = useState<StatsData>({});
  const [activeTab, setActiveTab] = useState('dates');

  // Filter states
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedCampaign, setSelectedCampaign] = useState<string>('');
  const [selectedClink, setSelectedClink] = useState<string>('');
  const [selectedPublisherId, setSelectedPublisherId] = useState<string>('');
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState<string>('');

  // Options for filters
  const [dates, setDates] = useState<string[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [clinks, setClinks] = useState<any[]>([]);
  const [publishers, setPublishers] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);

  // Helper functions to group options for selects
  const getGroupedCampaigns = () => {
    const groups: { [key: string]: any[] } = {};
    campaigns.forEach(campaign => {
      const groupKey = campaign.option_type || 'Searched Campaigns';
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(campaign);
    });
    return groups;
  };

  const getGroupedClinks = () => {
    const groups: { [key: string]: any[] } = {};
    clinks.forEach(clink => {
      const groupKey = clink.publisher_name || 'Unknown Publisher';
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(clink);
    });
    return groups;
  };

  const getGroupedCountries = () => {
    const groups: { [key: string]: any[] } = {};
    countries.forEach(country => {
      const groupKey = country.type || 'Countries';
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(country);
    });
    return groups;
  };

  const generateFallbackDates = (daysCount?: number) => {
    // Generate recent dates as fallback, using the provided count or default to 7
    const dateOptions: string[] = [];
    const date = new Date();
    const fallbackDays = daysCount || 7;
    
    for (let i = 0; i < fallbackDays; i++) {
      dateOptions.push(date.toISOString().split('T')[0]);
      date.setDate(date.getDate() - 1);
    }
    setDates(dateOptions);
  };

  const loadInitialData = useCallback(async () => {
    setLoading(true);

    // Set campaigns from app data
    let initialCampaigns: any[] = [];
    if (app.campaigns) {
      initialCampaigns = app.campaigns;
      setCampaigns(initialCampaigns);
    }

    // Set clinks from app data
    let initialClinks: any[] = [];
    if (app.clinks) {
      initialClinks = app.clinks;
      setClinks(initialClinks);
    }

    // Set countries from app data (targeted countries)
    let initialCountries: any[] = [];
    if (app.countries) {
      // app.countries might be a string like "US,CA,MX" or just "US"
      const countryList = typeof app.countries === 'string' 
        ? app.countries.split(',').map((c: string) => c.trim()).filter(Boolean)
        : Array.isArray(app.countries) ? app.countries : [];
      
      if (countryList.length > 0) {
        initialCountries = countryList.map((c: string) => ({
          countryCode: c,
          title: c,
          type: 'Targeted'
        }));
        setCountries(initialCountries);
      }
    }

    // Load stats data and pass initial data to avoid stale closure
    // This will also populate the dates based on the backend response
    await loadStatsData(initialCampaigns, initialClinks);

    setLoading(false);
    // Note: loadStatsData is called above but we can't include it in deps due to circular dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app]);

  const loadStatsData = useCallback(async (existingCampaigns?: any[], existingClinks?: any[]) => {
    setTableLoading(true);
    try {
      const params: any = {
        appID: app.appID,  // Changed from 'appid' to 'appID' to match Angular
        clinkID: selectedClink || '',  // Always include clinkID, empty if not selected
        countryCode: selectedCountry || ''  // Always include countryCode, empty if not selected
      };

      // Only add date filter if a specific date is selected
      if (selectedDate) {
        params.date = selectedDate;
      }
      // Note: For "Last 7 days", we don't send date parameters and filter on frontend instead

      if (selectedCampaign) {
        params.campaignID = selectedCampaign;  // Changed from 'campaignid' to 'campaignID'
      }
      if (selectedPublisherId) {
        params.publisherID = selectedPublisherId;
      }
      if (selectedSiteId) {
        params.siteID = selectedSiteId;
      }

      const url = joinURLs('admin/status/counters/summarized/data', process.env.REACT_APP_JUPITER_API_BASE_URL);

      const response = await axios.get(url, {...getHeadersConfig(), params});

      setData(response.data || {});

      // Only update dates array if no specific date is selected
      // This prevents the dropdown from changing when user filters by date
      if (!selectedDate) {
        // Extract available dates from the response data
        if (response.data.day && response.data.day.length > 0) {
          const availableDates = response.data.day
            .map((row: any) => {
              const dateValue = row.key?.date;
              if (!dateValue) return null;

              // Convert YYYYMMDD format to YYYY-MM-DD if needed
              if (dateValue.length === 8 && /^\d{8}$/.test(dateValue)) {
                const year = dateValue.substring(0, 4);
                const month = dateValue.substring(4, 6);
                const day = dateValue.substring(6, 8);
                return `${year}-${month}-${day}`;
              }

              return dateValue;
            })
            .filter(Boolean)
            .sort((a: string, b: string) => b.localeCompare(a)) // Sort descending (most recent first)
            .slice(0, 30); // Limit to last 30 days to avoid too many options

          if (availableDates.length > 0) {
            setDates(availableDates);
          } else {
            // Fallback: generate recent dates based on response data length
            const backendDataDays = response.data.day.length;
            generateFallbackDates(backendDataDays > 0 ? backendDataDays : 7);
          }
        } else {
          // Check if there's any other data to infer the time range
          let inferredDays = 7; // default

          // Try to infer from other data types (hour, campaign, etc.)
          if (response.data.hour && response.data.hour.length > 0) {
            // Hours data might give us a clue about the date range
            const uniqueHours = new Set(response.data.hour.map((h: any) => h.key?.date).filter(Boolean));
            inferredDays = Math.max(uniqueHours.size, 7);
          } else if (response.data.campaign && response.data.campaign.length > 0) {
            // If we have campaign data, assume a reasonable range
            inferredDays = Math.min(response.data.campaign.length, 14);
          }

          generateFallbackDates(inferredDays);
        }
      }

      // Extract unique campaigns from the data
      if (response.data.campaign) {
        const campaignsFromData = response.data.campaign.map((c: any) => ({
          campaignID: c.key?.campaignID,
          source: c.key?.source || 'Unknown'
        })).filter((c: any) => c.campaignID);

        // Merge with existing campaigns from app data using functional update
        setCampaigns(currentCampaigns => {
          const activeCampaigns = existingCampaigns || currentCampaigns;
          const existingCampaignIds = new Set(activeCampaigns.map((c: any) => c.campaignID || c.campaign_id));
          const newCampaigns = campaignsFromData.filter((c: any) => !existingCampaignIds.has(c.campaignID));

          if (newCampaigns.length > 0) {
            return [...activeCampaigns, ...newCampaigns];
          }
          return activeCampaigns;
        });
      }

      // Extract unique cLinks from the data
      if (response.data.clink) {
        const clinksFromData = response.data.clink.map((c: any) => ({
          clink_id: c.key?.clinkID,
          publisher_name: c.key?.publisherName || 'Unknown'
        })).filter((c: any) => c.clink_id);

        // Merge with existing clinks from app data using functional update
        setClinks(currentClinks => {
          const activeClinks = existingClinks || currentClinks || [];
          const existingClinkIds = new Set(activeClinks.map((c: any) => c.clink_id));
          const newClinks = clinksFromData.filter((c: any) => !existingClinkIds.has(c.clink_id));

          if (newClinks.length > 0 || (clinksFromData.length > 0 && activeClinks.length === 0)) {
            return activeClinks.length > 0 ? [...activeClinks, ...newClinks] : clinksFromData;
          }
          return activeClinks;
        });
      }

      // Extract unique publishers with site information from the data
      // Only update publishers list if it's empty (initial load)
      // This prevents the dropdown from being re-populated when filters change
      if (response.data.publisher && publishers.length === 0) {
        const trafficSources = StateHelper.getTrafficSourcesArray();
        const publisherData: any[] = [];

        response.data.publisher.forEach((p: any) => {
          const publisherId = p.key?.publisherID || p.key?.publisher_id;
          const siteId = p.key?.siteID || p.key?.site_id;
          const publisherName = p.key?.publisherName;

          if (publisherId) {
            let actualPublisherName = publisherName;
            let actualSiteName = siteId || publisherId;

            // Get proper names from traffic sources
            if (trafficSources) {
              const trafficSource = trafficSources.find((t: any) =>
                t.record && t.record.apptapPublisherId === publisherId
              );

              if (trafficSource && trafficSource.record) {
                actualPublisherName = trafficSource.record.apptapPublisherName || publisherName;

                // Type guard to check if it's a PublisherRecord (which has publisherSites)
                if (siteId && 'publisherSites' in trafficSource.record && trafficSource.record.publisherSites) {
                  const siteRecord = trafficSource.record.publisherSites.find((site: any) =>
                    site.apptapSiteId === siteId
                  );
                  if (siteRecord && siteRecord.apptapPublisherSiteName) {
                    actualSiteName = siteRecord.apptapPublisherSiteName;
                  }
                }
              }
            }

            // Only add entries that have both publisher and site IDs
            if (siteId) {
              publisherData.push({
                id: `${publisherId}:${siteId}`,
                publisherName: actualPublisherName || 'Unknown Publisher',
                siteName: actualSiteName || siteId || publisherId,
                publisherId,
                siteId
              });
            }
          }
        });

        // Remove duplicates based on id
        const uniquePublishers = publisherData.filter((pub, index, arr) =>
          arr.findIndex(p => p.id === pub.id) === index
        );

        setPublishers(uniquePublishers);
      }

      if (response.data.country) {
        const countryCodes = response.data.country.map((c: any) => c.key?.countryCode).filter(Boolean);
        const uniqueCountries = Array.from(new Set(countryCodes));
        const newCountries = uniqueCountries.map(c => ({countryCode: c, title: c, type: 'Targeted'}));
        
        // Merge with existing countries (from app data) using functional update
        setCountries(currentCountries => {
          const existingCountryCodes = new Set(currentCountries.map((c: any) => c.countryCode));
          const additionalCountries = newCountries.filter((c: any) => !existingCountryCodes.has(c.countryCode));
          
          if (additionalCountries.length > 0) {
            return [...currentCountries, ...additionalCountries];
          }
          return currentCountries;
        });
      }
    } catch (error) {
      // Error loading stats data
    } finally {
      setTableLoading(false);
    }
  }, [app, selectedDate, selectedCampaign, selectedClink, selectedPublisherId, selectedSiteId, selectedCountry, publishers.length]);

  // Reset state and load data when modal opens/closes or app changes
  useEffect(() => {
    if (show && app) {
      setSelectedDate('');
      setSelectedPublisherId('');
      setSelectedSiteId('');
      setSelectedCountry('');
      
      // If a campaign was selected from the campaign row, pre-select it
      if (selectedCampaignProp && (selectedCampaignProp.campaignID || selectedCampaignProp.campaign_id || selectedCampaignProp.dispCampaignID)) {
        const campaignId = selectedCampaignProp.campaignID || selectedCampaignProp.campaign_id || selectedCampaignProp.dispCampaignID;
        setSelectedCampaign(campaignId);
        setSelectedClink('');
      } else {
        setSelectedCampaign('');
        setSelectedClink('');
      }
      
      loadInitialData();
    }
  }, [show, app, selectedCampaignProp, loadInitialData]);

  // Watch for filter changes and reload data
  useEffect(() => {
    if (show && app && dates.length > 0) {
      loadStatsData();
    }
  }, [show, app, dates.length, loadStatsData]);

  const formatNumber = (num: number | undefined) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  const formatCurrency = (num: number | undefined) => {
    if (num === undefined || num === null) return '$0.00';
    return `$${num.toFixed(2)}`;
  };

  const formatPercentage = (num: number | undefined) => {
    if (num === undefined || num === null) return '0%';
    return `${(num * 100).toFixed(2)}%`;
  };

  const calculateStats = (values: number[]) => {
    const validValues = values.filter(v => !isNaN(v) && v > 0);
    if (validValues.length === 0) return { min: 0, max: 0, avg: 0 };
    
    return {
      min: Math.min(...validValues),
      max: Math.max(...validValues),
      avg: validValues.reduce((sum, v) => sum + v, 0) / validValues.length
    };
  };

  const renderTable = (rows: any[], keyCol: string, keyColName: string) => {
    if (!rows || rows.length === 0) {
      return <div className="text-center text-muted p-3">No data available</div>;
    }

    // Create a copy of rows to avoid mutating the original
    const rowsCopy = [...rows];

    // Filter out the total row (it usually has a key value of "Total" or similar)
    let filteredRows = rowsCopy.filter(row => {
      const keyValue = row.key?.[keyCol] || row[keyCol];
      // Also filter out empty key values but allow 0 for hours
      return keyValue !== 'Total' && keyValue !== 'TOTAL' && keyValue !== null && keyValue !== undefined && keyValue !== '';
    });

    // Apply appropriate sorting based on the key column type
    
    if (keyCol === 'date') {
      // Sort dates by most recent first (descending)
      filteredRows = [...filteredRows].sort((a, b) => {
        let dateValueA = '';
        let dateValueB = '';

        // Check multiple possible locations for date value
        if (a.key && typeof a.key === 'object' && 'date' in a.key) {
          dateValueA = a.key.date;
        } else if ('date' in a) {
          dateValueA = a.date;
        }

        if (b.key && typeof b.key === 'object' && 'date' in b.key) {
          dateValueB = b.key.date;
        } else if ('date' in b) {
          dateValueB = b.date;
        }

        if (!dateValueA || !dateValueB) {
          return 0; // Keep original order if no date
        }

        // Handle different date formats
        let dateA, dateB;
        
        // Check if it's YYYYMMDD format (8 digits)
        if (dateValueA.length === 8 && /^\d{8}$/.test(dateValueA)) {
          // Convert YYYYMMDD to YYYY-MM-DD format
          const year = dateValueA.substring(0, 4);
          const month = dateValueA.substring(4, 6);
          const day = dateValueA.substring(6, 8);
          dateA = new Date(`${year}-${month}-${day}`);
        } else {
          dateA = new Date(dateValueA);
        }
        
        if (dateValueB.length === 8 && /^\d{8}$/.test(dateValueB)) {
          // Convert YYYYMMDD to YYYY-MM-DD format
          const year = dateValueB.substring(0, 4);
          const month = dateValueB.substring(4, 6);
          const day = dateValueB.substring(6, 8);
          dateB = new Date(`${year}-${month}-${day}`);
        } else {
          dateB = new Date(dateValueB);
        }
        
        return dateB.getTime() - dateA.getTime(); // Descending order (most recent first)
      });
    } else if (keyCol === 'hour') {
      // Sort hours by hour value (23-0, descending like original AngularJS)
      filteredRows = [...filteredRows].sort((a, b) => {
        const hourA = parseInt(a.key?.hour || a.hour || '0');
        const hourB = parseInt(b.key?.hour || b.hour || '0');
        return hourB - hourA; // Descending order (highest hour first)
      });
    } else if (keyColName === 'Date/Hour') {
      // Sort date/hour combination by most recent date first, then by hour ascending
      filteredRows = [...filteredRows].sort((a, b) => {
        const dateA = new Date(a.key?.date || '');
        const dateB = new Date(b.key?.date || '');
        const dateDiff = dateB.getTime() - dateA.getTime();

        // If dates are the same, sort by hour ascending
        if (dateDiff === 0) {
          const hourA = parseInt(a.key?.hour || '0');
          const hourB = parseInt(b.key?.hour || '0');
          return hourA - hourB;
        }

        return dateDiff; // Most recent date first
      });
    } else {
      // For other tabs (publishers, countries, clinks, campaigns), sort by installs descending (highest performers first)
      filteredRows = [...filteredRows].sort((a, b) => {
        const installsA = a.installs || 0;
        const installsB = b.installs || 0;
        if (installsA !== installsB) {
          return installsB - installsA; // Sort by installs descending
        }
        
        // If installs are equal, sort by the key value alphabetically
        const keyA = (a.key?.[keyCol] || a[keyCol] || '').toString().toLowerCase();
        const keyB = (b.key?.[keyCol] || b[keyCol] || '').toString().toLowerCase();
        return keyA.localeCompare(keyB);
      });
    }

    if (filteredRows.length === 0) {
      return <div className="text-center text-muted p-3">No data available</div>;
    }

    // Calculate statistics for tooltips
    const revenueStats = calculateStats(filteredRows.map(row => row.revenue || 0));
    const publisherCostStats = calculateStats(filteredRows.map(row => (row.payout || 0) + (row.clicksCost || 0)));
    const netRevenueStats = calculateStats(filteredRows.map(row => (row.revenue || 0) - ((row.payout || 0) + (row.clicksCost || 0))));
    const avgPublisherCPCStats = calculateStats(filteredRows.map(row => {
      const totalClicks = (row.trackedBuyClicks || 0) + (row.untrackedBuyClicks || 0);
      return totalClicks > 0 ? ((row.payout || 0) + (row.clicksCost || 0)) / totalClicks : 0;
    }));
    const avgRevenueCPAStats = calculateStats(filteredRows.map(row => row.installs > 0 ? (row.revenue || 0) / row.installs : 0));
    const avgRevenueECPCStats = calculateStats(filteredRows.map(row => {
      const totalClicks = (row.trackedBuyClicks || 0) + (row.untrackedBuyClicks || 0);
      return totalClicks > 0 ? (row.revenue || 0) / totalClicks : 0;
    }));

    // Calculate totals from filtered rows only
    const totals = filteredRows.reduce((acc, row) => {
      acc.appImpressions = (acc.appImpressions || 0) + (row.appImpressions || 0);
      acc.trackedBuyClicks = (acc.trackedBuyClicks || 0) + (row.trackedBuyClicks || 0);
      acc.untrackedBuyClicks = (acc.untrackedBuyClicks || 0) + (row.untrackedBuyClicks || 0);
      acc.installs = (acc.installs || 0) + (row.installs || 0);
      acc.revenue = (acc.revenue || 0) + (row.revenue || 0);
      acc.payout = (acc.payout || 0) + (row.payout || 0);
      acc.clicksCost = (acc.clicksCost || 0) + (row.clicksCost || 0);
      return acc;
    }, {});

    return (
        <div className="table-responsive">
          <Table striped bordered hover size="sm" className="stats-table">
            <thead>
            <tr>
              <th style={{ minWidth: '120px' }}>{keyColName}</th>
              <th className="text-end" style={{ minWidth: '80px' }}>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="impressions-tooltip">Impressions recorded for this product. Not all placements are able to count impressions</Tooltip>}
                >
                  <span>impressions</span>
                </OverlayTrigger>
              </th>
              <th className="text-end">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tracked-clicks-tooltip">Clicks sent an advertiser campaign</Tooltip>}
                >
                  <span>Tracked<br/>Clicks</span>
                </OverlayTrigger>
              </th>
              <th className="text-end">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="untracked-clicks-tooltip">Clicks that could not be sent to a campaign</Tooltip>}
                >
                  <span>Untracked<br/>Clicks</span>
                </OverlayTrigger>
              </th>
              <th className="text-end">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="conversions-tooltip">Conversions. Credited events (installs, post-install events, or credited clicks for CPC)</Tooltip>}
                >
                  <span>Conv.</span>
                </OverlayTrigger>
              </th>
              <th className="text-end">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="conversion-rate-tooltip">Conversion rate (conversions / tracked clicks).</Tooltip>}
                >
                  <span>CR</span>
                </OverlayTrigger>
              </th>
              <th className="text-end">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="revenue-tooltip">Recorded revenue for credited conversions</Tooltip>}
                >
                  <span>Revenue</span>
                </OverlayTrigger>
              </th>
              <th className="text-end">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="publisher-payout-tooltip">Either cost reported by the publisher or recorded payout to publishers for credited conversions</Tooltip>}
                >
                  <span>Publisher<br/>Payout/Cost</span>
                </OverlayTrigger>
              </th>
              <th className="text-end">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="net-revenue-tooltip">Recorded gross revenue minus any reported costs or recorded publisher payouts.</Tooltip>}
                >
                  <span>Net Revenue</span>
                </OverlayTrigger>
              </th>
              <th className="text-end">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="avg-publisher-cpc-tooltip">Average reported cost per click based on publisher partner's reported cost. Average based only on clicks with reported costs greater than zero.</Tooltip>}
                >
                  <span>Avg Publisher<br/>Payout/CPC</span>
                </OverlayTrigger>
              </th>
              <th className="text-end">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="avg-revenue-cpa-tooltip">Average revenue payout per credited conversion (eCPI for app install campaigns, eCPC for CPC campaigns)</Tooltip>}
                >
                  <span>Avg Revenue<br/>CPA</span>
                </OverlayTrigger>
              </th>
              <th className="text-end">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="avg-revenue-ecpc-tooltip">Average effective revenue payout CPC based all clicks, tracked and untracked. (effective cost per click)</Tooltip>}
                >
                  <span>Avg Revenue<br/>eCPC</span>
                </OverlayTrigger>
              </th>
            </tr>
            </thead>
            <tbody>
            {filteredRows.map((row, index) => {
              const cr = row.trackedBuyClicks > 0 ? row.installs / row.trackedBuyClicks : 0;
              const publisherCost = (row.payout || 0) + (row.clicksCost || 0);
              const netRevenue = (row.revenue || 0) - publisherCost;
              const totalClicks = (row.trackedBuyClicks || 0) + (row.untrackedBuyClicks || 0);
              const avgPublisherCPC = totalClicks > 0 ? publisherCost / totalClicks : 0;
              const avgRevenueCPA = row.installs > 0 ? (row.revenue || 0) / row.installs : 0;
              const avgRevenueECPC = totalClicks > 0 ? (row.revenue || 0) / totalClicks : 0;
              let keyValue = row.key?.[keyCol] || row[keyCol] || '-';

              // Format the key value based on the column type
              let displayValue = keyValue;
              if (keyColName === 'Date' && keyValue !== '-') {
                displayValue = dayjs(keyValue).format('YYYY-MM-DD ddd');
              } else if (keyColName === 'Hour' && keyValue !== '-') {
                displayValue = `${keyValue} GMT`;
              } else if (keyColName === 'Date/Hour' && row.key?.date) {
                displayValue = `${dayjs(row.key.date).format('YYYY-MM-DD')} ${row.key.hour || 0}:00`;
              } else if (keyColName === 'cLinkID' && row.key?.publisherName) {
                // For cLinks, show publisher name above the cLink ID
                displayValue = (
                    <div>
                      <div>{row.key.publisherName}</div>
                      <div style={{fontSize: '0.9em', color: '#666666'}}>{keyValue}</div>
                    </div>
                );
              }

              return (
                  <tr key={index}>
                    <td>{displayValue}</td>
                    <td className="text-end">{formatNumber(row.appImpressions)}</td>
                    <td className="text-end">{formatNumber(row.trackedBuyClicks)}</td>
                    <td className="text-end">{formatNumber(row.untrackedBuyClicks)}</td>
                    <td className="text-end">{formatNumber(row.installs)}</td>
                    <td className="text-end">{formatPercentage(cr)}</td>
                    <td className="text-end">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="revenue-tooltip">
                            Revenue across dataset:<br/>
                            Avg: {formatCurrency(revenueStats.avg)}<br/>
                            Min: {formatCurrency(revenueStats.min)}<br/>
                            Max: {formatCurrency(revenueStats.max)}
                          </Tooltip>
                        }
                      >
                        <span>{formatCurrency(row.revenue)}</span>
                      </OverlayTrigger>
                    </td>
                    <td className="text-end">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="publisher-cost-tooltip">
                            Avg Publisher cost/payout per click:<br/>
                            Avg: {formatCurrency(publisherCostStats.avg)}<br/>
                            Min: {formatCurrency(publisherCostStats.min)}<br/>
                            Max: {formatCurrency(publisherCostStats.max)}
                          </Tooltip>
                        }
                      >
                        <span>{formatCurrency(publisherCost)}</span>
                      </OverlayTrigger>
                    </td>
                    <td className="text-end">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="net-revenue-tooltip">
                            Net Revenue across dataset:<br/>
                            Avg: {formatCurrency(netRevenueStats.avg)}<br/>
                            Min: {formatCurrency(netRevenueStats.min)}<br/>
                            Max: {formatCurrency(netRevenueStats.max)}
                          </Tooltip>
                        }
                      >
                        <span>{formatCurrency(netRevenue)}</span>
                      </OverlayTrigger>
                    </td>
                    <td className="text-end">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="avg-publisher-cpc-data-tooltip">
                            Avg Publisher cost/payout per click:<br/>
                            Avg: {formatCurrency(avgPublisherCPCStats.avg)}<br/>
                            Min: {formatCurrency(avgPublisherCPCStats.min)}<br/>
                            Max: {formatCurrency(avgPublisherCPCStats.max)}
                          </Tooltip>
                        }
                      >
                        <span>{formatCurrency(avgPublisherCPC)}</span>
                      </OverlayTrigger>
                    </td>
                    <td className="text-end">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="avg-revenue-cpa-data-tooltip">
                            Avg Revenue per Acquisition:<br/>
                            Avg: {formatCurrency(avgRevenueCPAStats.avg)}<br/>
                            Min: {formatCurrency(avgRevenueCPAStats.min)}<br/>
                            Max: {formatCurrency(avgRevenueCPAStats.max)}
                          </Tooltip>
                        }
                      >
                        <span>{formatCurrency(avgRevenueCPA)}</span>
                      </OverlayTrigger>
                    </td>
                    <td className="text-end">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="avg-revenue-ecpc-data-tooltip">
                            Avg Revenue per Click:<br/>
                            Avg: {formatCurrency(avgRevenueECPCStats.avg)}<br/>
                            Min: {formatCurrency(avgRevenueECPCStats.min)}<br/>
                            Max: {formatCurrency(avgRevenueECPCStats.max)}
                          </Tooltip>
                        }
                      >
                        <span>{formatCurrency(avgRevenueECPC)}</span>
                      </OverlayTrigger>
                    </td>
                  </tr>
              );
            })}
            </tbody>
            {filteredRows.length > 1 && (
                <tfoot>
                <tr className="font-weight-bold">
                  <td>Total</td>
                  <td className="text-end">{formatNumber(totals.appImpressions)}</td>
                  <td className="text-end">{formatNumber(totals.trackedBuyClicks)}</td>
                  <td className="text-end">{formatNumber(totals.untrackedBuyClicks)}</td>
                  <td className="text-end">{formatNumber(totals.installs)}</td>
                  <td className="text-end">
                    {formatPercentage(totals.trackedBuyClicks > 0 ? totals.installs / totals.trackedBuyClicks : 0)}
                  </td>
                  <td className="text-end">{formatCurrency(totals.revenue)}</td>
                  <td className="text-end">{formatCurrency((totals.payout || 0) + (totals.clicksCost || 0))}</td>
                  <td className="text-end">{formatCurrency(totals.revenue - ((totals.payout || 0) + (totals.clicksCost || 0)))}</td>
                  <td className="text-end">{formatCurrency((totals.trackedBuyClicks + totals.untrackedBuyClicks) > 0 ? ((totals.payout || 0) + (totals.clicksCost || 0)) / (totals.trackedBuyClicks + totals.untrackedBuyClicks) : 0)}</td>
                  <td className="text-end">{formatCurrency(totals.installs > 0 ? totals.revenue / totals.installs : 0)}</td>
                  <td className="text-end">{formatCurrency((totals.trackedBuyClicks + totals.untrackedBuyClicks) > 0 ? totals.revenue / (totals.trackedBuyClicks + totals.untrackedBuyClicks) : 0)}</td>
                </tr>
                </tfoot>
            )}
          </Table>
        </div>
    );
  };

  const getDataRowsCount = (rows: any[], keyCol: string) => {
    if (!rows || rows.length === 0) return 0;

    // Filter out the total row, same logic as in renderTable
    const filteredRows = rows.filter(row => {
      const keyValue = row.key?.[keyCol] || row[keyCol];
      return keyValue !== 'Total' && keyValue !== 'TOTAL' && keyValue !== null && keyValue !== undefined && keyValue !== '';
    });

    return filteredRows.length;
  };

  const buildDashboardUrl = () => {
    // Build URL parameters based on current filters
    const params: string[] = [];

    // Date parameters
    if (selectedDate) {
      params.push(`dateStart=${selectedDate}`);
      params.push(`dateEnd=${selectedDate}`);
    } else {
      // Use the actual date range from the dates array
      if (dates.length > 0) {
        const endDate = dates[0]; // Most recent date (dates are sorted descending)
        const startDate = dates[dates.length - 1]; // Oldest date
        params.push(`dateStart=${startDate}`);
        params.push(`dateEnd=${endDate}`);
      } else {
        // Fallback to 7 days if dates array is empty
        const endDate = dayjs().format('YYYY-MM-DD');
        const startDate = dayjs().subtract(6, 'day').format('YYYY-MM-DD');
        params.push(`dateStart=${startDate}`);
        params.push(`dateEnd=${endDate}`);
      }
    }

    // Product filter
    if (app?.appID) {
      params.push(`products${IncludeExclude.Include}=${encodeURIComponent(app.appID)}`);
    }

    // Campaign filter - split into demandSourceId and campaignId
    if (selectedCampaign) {
      // Find the campaign object to get its source
      const campaign = campaigns.find(c =>
        (c.campaignID || c.campaign_id) === selectedCampaign
      );

      if (campaign) {
        const demandSourceId = campaign.source || campaign.supplySource;
        const campaignId = campaign.campaignID || campaign.campaign_id;

        if (demandSourceId) {
          params.push(`demandSourceIds${IncludeExclude.Include}=${encodeURIComponent(demandSourceId)}`);
        }
        if (campaignId) {
          params.push(`campaignIds${IncludeExclude.Include}=${encodeURIComponent(campaignId)}`);
        }
      } else {
        // Fallback: if we can't find the campaign object, just use campaignId
        params.push(`campaignIds${IncludeExclude.Include}=${encodeURIComponent(selectedCampaign)}`);
      }
    }

    // cLink filter
    if (selectedClink) {
      params.push(`clinks${IncludeExclude.Include}=${encodeURIComponent(selectedClink)}`);
    }

    // Publisher/Site filter
    if (selectedSiteId) {
      // When siteID is selected, only send siteIds (since siteIds are globally unique)
      params.push(`siteIds${IncludeExclude.Include}=${encodeURIComponent(selectedSiteId)}`);
    } else if (selectedPublisherId) {
      // When only publisherID is selected, send publisherIds (all sites for that publisher)
      params.push(`publisherIds${IncludeExclude.Include}=${encodeURIComponent(selectedPublisherId)}`);
    }

    // Country filter
    if (selectedCountry) {
      params.push(`countries${IncludeExclude.Include}=${encodeURIComponent(selectedCountry)}`);
    }

    // Group by date by default
    params.push('groupBy=date');

    // Build the full URL with proper base path handling
    const baseUrl = window.location.origin;
    const basePath = PagesHelper.getBasePath();
    return `${baseUrl}${basePath}products?${params.join('&')}`;
  };

  return (
      <>
        <Modal
            show={show}
            onHide={onHide}
            className="stats-modal"
            dialogClassName="modal-xl"
            size="xl"
        >
        <Modal.Header closeButton>
          <Modal.Title>
            Stats for {app?.title}{' '}
            <InfoIcon iconSize="18px">
              <div style={{ textAlign: 'left' }}>
                <strong>Real-time data from Jupiter</strong><br/>
                This modal shows live statistics from in-memory counters, providing near real-time metrics.<br/><br/>
                <strong>Different from Reporting Dashboard:</strong><br/>
                The main Reporting Dashboard uses historical data that may have a delay but provides
                comprehensive reporting capabilities.
              </div>
            </InfoIcon>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
              <Loading />
          ) : (
              <>
                {/* Filters */}
                <div className="mb-4" style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                  <div className="row mb-1">
                    <label className="col-sm-2 col-form-label col-form-label-sm fw-bold" style={{ paddingBottom: '2px' }}>ProductID</label>
                    <div className="col-sm-10">
                      <input type="text" readOnly className="form-control-plaintext form-control-sm" value={app?.appID || ''} style={{ paddingTop: '2px', paddingBottom: '2px' }} />
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-sm-2 col-form-label col-form-label-sm fw-bold" style={{ paddingBottom: '2px' }}>Date</label>
                    <div className="col-sm-10">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Form.Control
                            as="select"
                            value={selectedDate}
                            onChange={(e) => {
                              setSelectedDate(e.target.value);
                            }}
                            style={{ flex: 1, maxWidth: '200px', cursor: 'pointer' }}
                            size="sm"
                        >
                          <option value="">Last {dates.length} days</option>
                          {dates.map(date => (
                              <option key={date} value={date}>{date}</option>
                          ))}
                        </Form.Control>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              setSelectedDate(dates[0]);
                            }}
                            title="Show today's data"
                        >
                          <i className="fas fa-calendar-day"></i>
                        </Button>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => {
                              setSelectedDate('');
                            }}
                            title={`Show the last ${dates.length} days data`}
                        >
                          <i className="fas fa-calendar-week"></i>
                        </Button>
                        {selectedDate && (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => {
                              setSelectedDate('');
                            }}
                            style={{ padding: '2px 8px', color: '#dc3545' }}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-sm-2 col-form-label col-form-label-sm fw-bold" style={{ paddingBottom: '2px' }}>Campaign</label>
                    <div className="col-sm-10">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Form.Control
                          as="select"
                          value={selectedCampaign}
                          onChange={(e) => setSelectedCampaign(e.target.value)}
                          style={{ flex: 1, maxWidth: '400px', cursor: 'pointer' }}
                          size="sm"
                        >
                          <option value="">All Campaigns</option>
                          {Object.entries(getGroupedCampaigns()).map(([groupName, groupCampaigns]) => (
                            <optgroup key={groupName} label={groupName}>
                              {groupCampaigns.map((campaign) => {
                                const campaignId = campaign.campaignID || campaign.campaign_id;
                                const sourceId = campaign.source || campaign.supplySource;
                                const sourceName = PagesHelper.getDemandSourceLabel(sourceId, false) || sourceId || 'Unknown';
                                const displayText = `${sourceName}: ${campaignId}`;
                                return (
                                  <option key={campaignId} value={campaignId} label={displayText} />
                                );
                              })}
                            </optgroup>
                          ))}
                        </Form.Control>
                        {selectedCampaign && (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => setSelectedCampaign('')}
                            style={{ padding: '2px 8px', color: '#dc3545' }}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-sm-2 col-form-label col-form-label-sm fw-bold" style={{ paddingBottom: '2px' }}>cLink ID</label>
                    <div className="col-sm-10">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Form.Control
                          as="select"
                          value={selectedClink}
                          onChange={(e) => setSelectedClink(e.target.value)}
                          style={{ flex: 1, maxWidth: '400px', cursor: 'pointer' }}
                          size="sm"
                          className="stats-select"
                        >
                          <option value="">All cLinks</option>
                          {clinks.length > 0 && Object.entries(getGroupedClinks()).map(([groupName, groupClinks]) => (
                            <optgroup key={groupName} label={groupName}>
                              {groupClinks.map((clink) => (
                                <option key={clink.clink_id} value={clink.clink_id} label={clink.clink_id} />
                              ))}
                            </optgroup>
                          ))}
                        </Form.Control>
                        {selectedClink && (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => setSelectedClink('')}
                            style={{ padding: '2px 8px', color: '#dc3545' }}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-sm-2 col-form-label col-form-label-sm fw-bold" style={{ paddingBottom: '2px' }}>Site ID</label>
                    <div className="col-sm-10">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Form.Control
                          as="select"
                          value={selectedSiteId ? `${selectedPublisherId}:${selectedSiteId}` : ''}
                          onChange={(e) => {
                            if (e.target.value) {
                              const [publisherId, siteId] = e.target.value.split(':');
                              setSelectedPublisherId(publisherId);
                              setSelectedSiteId(siteId);
                            } else {
                              setSelectedPublisherId('');
                              setSelectedSiteId('');
                            }
                          }}
                          style={{ flex: 1, maxWidth: '400px', cursor: 'pointer' }}
                          size="sm"
                          className="stats-select"
                        >
                          <option value="">All Sites</option>
                          {(() => {
                            // Group publishers by publisher name
                            const groupedPublishers: { [key: string]: any[] } = {};
                            publishers.forEach((pub) => {
                              const groupKey = pub.publisherName || 'Unknown Publisher';
                              if (!groupedPublishers[groupKey]) {
                                groupedPublishers[groupKey] = [];
                              }
                              groupedPublishers[groupKey].push(pub);
                            });

                            return Object.entries(groupedPublishers).map(([publisherName, sites]) => (
                              <optgroup key={publisherName} label={publisherName}>
                                {sites.map((site) => (
                                  <option key={site.id} value={site.id}>
                                    {site.siteName}
                                  </option>
                                ))}
                              </optgroup>
                            ));
                          })()}
                        </Form.Control>
                        {selectedSiteId && (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => {
                              setSelectedPublisherId('');
                              setSelectedSiteId('');
                            }}
                            style={{ padding: '2px 8px', color: '#dc3545' }}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="row mb-1">
                    <label className="col-sm-2 col-form-label col-form-label-sm fw-bold" style={{ paddingBottom: '2px' }}>Country</label>
                    <div className="col-sm-10">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Form.Control
                          as="select"
                          value={selectedCountry}
                          onChange={(e) => setSelectedCountry(e.target.value)}
                          style={{ flex: 1, maxWidth: '200px', cursor: 'pointer' }}
                          size="sm"
                          className="stats-select"
                        >
                          <option value="">All Countries</option>
                          {Object.entries(getGroupedCountries()).map(([groupName, groupCountries]) => (
                            <optgroup style={{ left: "-30px", paddingLeft: 0, marginLeft: 0}} key={groupName} label={groupName}>
                              {groupCountries.map((country) => (
                                <option key={country.countryCode} value={country.countryCode} label={country.title} />
                              ))}
                            </optgroup>
                          ))}
                        </Form.Control>
                        {selectedCountry && (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => setSelectedCountry('')}
                            style={{ padding: '2px 8px', color: '#dc3545' }}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'dates')}>
                  <Nav variant="tabs">
                    <Nav.Item>
                      <Nav.Link eventKey="dates">Days ({getDataRowsCount(data.day, 'date')})</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="hours">Hours ({getDataRowsCount(data.hour, 'hour')})</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="day_hour">Day-Hour ({getDataRowsCount(data.day_hour, 'date')})</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="publishers">Publishers
                                                      ({getDataRowsCount(data.publisher, 'publisherName')})</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="countries">Countries
                                                     ({getDataRowsCount(data.country, 'countryCode')})</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="clinks">cLinks ({getDataRowsCount(data.clink, 'clinkID')})</Nav.Link>
                    </Nav.Item>
                    {currentUser?.permissions?.includes('apptap:data:campaigns') && (
                        <Nav.Item>
                          <Nav.Link eventKey="campaigns">Campaigns
                                                         ({getDataRowsCount(data.campaign, 'campaignID')})</Nav.Link>
                        </Nav.Item>
                    )}
                  </Nav>
                  <Tab.Content className="pt-3">
                    {tableLoading ? (
                        <div className="text-center p-4">
                          <Loading />
                        </div>
                    ) : (
                        <>
                          <Tab.Pane eventKey="dates">
                            {renderTable(data.day || [], 'date', 'Date')}
                          </Tab.Pane>
                          <Tab.Pane eventKey="hours">
                            {renderTable(data.hour || [], 'hour', 'Hour')}
                          </Tab.Pane>
                          <Tab.Pane eventKey="day_hour">
                            {renderTable(data.day_hour || [], 'date', 'Date/Hour')}
                          </Tab.Pane>
                          <Tab.Pane eventKey="publishers">
                            {renderTable(data.publisher || [], 'publisherName', 'Publisher')}
                          </Tab.Pane>
                          <Tab.Pane eventKey="countries">
                            {renderTable(data.country || [], 'countryCode', 'Country')}
                          </Tab.Pane>
                          <Tab.Pane eventKey="clinks">
                            {renderTable(data.clink || [], 'clinkID', 'cLinkID')}
                          </Tab.Pane>
                          {currentUser?.permissions?.includes('apptap:data:campaigns') && (
                              <Tab.Pane eventKey="campaigns">
                                {renderTable(data.campaign || [], 'campaignID', 'CampaignID')}
                              </Tab.Pane>
                          )}
                        </>
                    )}
                  </Tab.Content>
                </Tab.Container>
              </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
              variant="primary"
              href={buildDashboardUrl()}
              target="_blank"
          >
            Open Reporting Dashboard
          </Button>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      </>
  );
};

export default StatsModal;